<template>
  <div class="mask" @click.self="onClickCancel">
    <div
      ref="dialogRoot"
      class="dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="dialog__header">
        <button
          type="button"
          class="dialog__edit"
          @click="onClickEdit"
        >
          {{ $t("proofreadingProperNounExcludeDialog.edit") }}
        </button>
        <h2 :id="titleId" class="dialog__title">
          {{ $t("proofreadingProperNounExcludeDialog.title") }}
        </h2>
        <button
          type="button"
          class="dialog__close"
          :aria-label="$t('proofreadingProperNounExcludeDialog.close')"
          @click="onClickCancel"
        >
          ×
        </button>
      </header>

      <div class="dialog__body">
        <div v-if="isLoading" class="dialog__loading">
          {{ $t("proofreadingProperNounExcludeDialog.loading") }}
        </div>
        <template v-else>
          <button type="button" class="row row--select-all" @click="onToggleSelectAll">
            <span class="select-all-box" :data-checked="isAllSelected">
              <CheckboxIcon :is-checked="isAllSelected" :fill="checkboxFill(isAllSelected)" />
            </span>
            <span class="label">{{ $t("proofreadingProperNounExcludeDialog.selectAll") }}</span>
          </button>

          <p v-if="properNouns.length === 0" class="dialog__empty">
            {{ $t("proofreadingProperNounExcludeDialog.empty") }}
          </p>

          <ul v-else class="noun-list">
            <li
              v-for="noun in properNouns"
              :key="noun.properNounId"
              class="row row--noun"
              @click="onToggleNoun(noun.properNounId)"
            >
              <span
                class="switch"
                role="switch"
                :aria-checked="isSelected(noun.properNounId)"
                :data-on="isSelected(noun.properNounId)"
              >
                <span class="switch__knob" />
              </span>
              <span class="label">{{ noun.name }}</span>
            </li>
          </ul>
        </template>
      </div>

      <footer class="dialog__footer">
        <button type="button" class="button button--primary" @click="onClickConfirm">
          {{ $t("proofreadingProperNounExcludeDialog.confirm") }}
        </button>
        <button type="button" class="button button--secondary" @click="onClickCancel">
          {{ $t("proofreadingProperNounExcludeDialog.cancel") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import CheckboxIcon from "@/components/atoms/CheckboxIcon.vue";
import type { ProperNoun } from "@/lib/models/properNoun";

export type ProofreadingProperNounExcludeDialogResult =
  | { confirmed: true; selectedIds: string[] }
  | { confirmed: false; openManager?: boolean };

export type ProofreadingProperNounExcludeDialogOptions = {
  properNouns: ProperNoun[];
  /** null = 全選択扱い */
  initialSelectedIds: string[] | null;
  isLoading?: boolean;
};

export default Vue.extend({
  components: {
    CheckboxIcon,
  },
  props: {
    properNouns: {
      type: Array as PropType<ProperNoun[]>,
      default: () => [],
    },
    initialSelectedIds: {
      type: Array as PropType<string[] | null>,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const allIds = (this.properNouns as ProperNoun[]).map((p) => p.properNounId);
    const initial = this.initialSelectedIds as string[] | null;
    const selectedIds =
      initial == null ? [...allIds] : allIds.filter((id) => initial.includes(id));
    return {
      titleId: "proofreading-proper-noun-exclude-dialog-title",
      checkedFill: "var(--color-primary)",
      uncheckedFill: "var(--color-inactive)",
      selectedIds,
    };
  },
  computed: {
    selectedIdSet(): Set<string> {
      return new Set(this.selectedIds);
    },
    isAllSelected(): boolean {
      if (this.properNouns.length === 0) return false;
      return this.properNouns.every((p: ProperNoun) => this.selectedIdSet.has(p.properNounId));
    },
  },
  watch: {
    properNouns: {
      handler(next: ProperNoun[]) {
        const allIds = next.map((p) => p.properNounId);
        if (this.initialSelectedIds == null) {
          this.selectedIds = [...allIds];
          return;
        }
        // 管理画面から戻って新規追加された語は、個別選択モードでも除外対象に含める
        const previouslySelected = new Set(this.selectedIds as string[]);
        const previouslyKnown = new Set(this.initialSelectedIds as string[]);
        this.selectedIds = allIds.filter(
          (id) => previouslySelected.has(id) || !previouslyKnown.has(id),
        );
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      const root = this.$refs.dialogRoot as HTMLElement | undefined;
      root?.focus();
    });
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    checkboxFill(checked: boolean): string {
      return checked ? this.checkedFill : this.uncheckedFill;
    },
    isSelected(id: string): boolean {
      return this.selectedIdSet.has(id);
    },
    onToggleNoun(id: string) {
      if (this.selectedIdSet.has(id)) {
        this.selectedIds = this.selectedIds.filter((x: string) => x !== id);
      } else {
        this.selectedIds = [...this.selectedIds, id];
      }
    },
    onToggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedIds = [];
      } else {
        this.selectedIds = this.properNouns.map((p: ProperNoun) => p.properNounId);
      }
    },
    onKeydown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        this.onClickCancel();
      }
    },
    onClickConfirm() {
      this.$close({
        confirmed: true,
        selectedIds: [...this.selectedIds],
      } as ProofreadingProperNounExcludeDialogResult);
    },
    onClickCancel() {
      this.$close({
        confirmed: false,
      } as ProofreadingProperNounExcludeDialogResult);
    },
    onClickEdit() {
      this.$close({
        confirmed: false,
        openManager: true,
      } as ProofreadingProperNounExcludeDialogResult);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9100;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(420px, 92vw);
  max-height: min(calc(100vh - 96px), 720px);
  margin: 0;
  border: none;
  background-color: var(--color-surface-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
  overflow: hidden;

  &:focus {
    outline: none;
  }
}

.dialog__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  padding: 12px 48px;
  border-bottom: solid 1px var(--color-border);
}

.dialog__title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.dialog__edit {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  padding: 4px 0;
}

.dialog__close {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-rounded);
  background: transparent;
  color: var(--color-text-normal);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;

  @include hover-overlay;
}

.dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 16px 16px;
}

.dialog__loading,
.dialog__empty {
  margin: 24px 0;
  text-align: center;
  color: var(--color-text-low);
  font-size: var(--font-size-sm);
}

.noun-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 8px 12px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  user-select: none;
  background-color: var(--color-background-normal);
}

.row--select-all {
  margin-bottom: 8px;
  background-color: transparent;
}

.label {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.select-all-box {
  display: inline-flex;
  line-height: 0;
}

.switch {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: 44px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--color-inactive, #cfcfcf);
  transition: background-color 0.15s ease;

  &[data-on="true"] {
    background-color: var(--color-primary);
  }
}

.switch__knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease;

  .switch[data-on="true"] & {
    transform: translateX(18px);
  }
}

.dialog__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 20px 20px;
  border-top: solid 1px var(--color-border);
}

.button {
  width: 100%;
  border: none;
  border-radius: var(--radius-rounded);
  padding: 14px 24px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
}

.button--primary {
  background-color: var(--color-primary);
  color: var(--color-text-invert);
}

.button--secondary {
  background-color: var(--color-surface-base, #ececec);
  color: var(--color-text-normal);
}

@include mq("sp") {
  .dialog {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
}
</style>
